import { MenuItem, Select } from "@mui/material";
import { Icon } from "@/components/icons";
import { formatNumber } from "@/app/(app)/_lib/format";

type PaginationFooterProps = {
  total: number;
  /** Per-type breakdown row. Omit all three (e.g. the Adverse Media results
   *  table, which only ever has one type) to render just the total. */
  individuals?: number;
  organizations?: number;
  others?: number;
  /** Plural label for the leading total. Defaults to "names". */
  totalLabel?: string;
  /** Compact mode (split detail view): centers controls, hides the stats row. */
  compact?: boolean;
  /** Current 1-based page. Wires the page controls when paired with pageCount + onPageChange. */
  page?: number;
  /** Total number of pages. */
  pageCount?: number;
  onPageChange?: (page: number) => void;
  /** When present, renders a "Rows per page" selector at the far right. */
  rowsPerPage?: number;
  rowsPerPageOptions?: readonly number[];
  onRowsPerPageChange?: (n: number) => void;
};

const DEFAULT_ROWS_PER_PAGE_OPTIONS: readonly number[] = [10, 25, 50, 100];

/**
 * Footer pagination strip — stats row + page controls + rows-per-page.
 *
 * Page controls are wired when `page` + `pageCount` + `onPageChange` are all
 * supplied: the prev/next buttons step within bounds and the numbered pages
 * navigate directly (with ellipsis collapsing long ranges). When those props
 * are absent the strip falls back to the static "1" placeholder (batch /
 * screening haven't wired client-side paging yet). This stays the swap point
 * for `<MuiPagination>` once the API returns server-paginated data.
 *
 * The rows-per-page selector renders an `@mui/material/Select` so
 * keyboard/a11y/MUI theming flow through the same surface.
 */
export function PaginationFooter({
  total,
  individuals,
  organizations,
  others,
  totalLabel = "names",
  compact = false,
  page,
  pageCount,
  onPageChange,
  rowsPerPage,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  onRowsPerPageChange,
}: PaginationFooterProps) {
  const wired = page !== undefined && pageCount !== undefined && onPageChange !== undefined;
  const pager = wired ? (
    <Pager page={page} pageCount={pageCount} onPageChange={onPageChange} />
  ) : (
    <StaticPager />
  );

  if (compact) {
    return (
      <div className="border-t border-grey-200 bg-grey-25 p-2">
        <div className="flex items-center justify-center gap-1 rounded-[4px] border border-grey-200 bg-white px-3 py-1.5">
          {pager}
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-grey-200 bg-grey-25 p-2">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-[4px] border border-grey-200 bg-white px-3 py-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-tiny">
          <span className="font-medium text-black">
            {formatNumber(total)} {total === 1 ? singularize(totalLabel) : totalLabel}
          </span>
          {individuals !== undefined && organizations !== undefined && others !== undefined && (
            <>
              <Sep />
              <Stat n={individuals} label="individuals" />
              <Sep />
              <Stat n={organizations} label="organizations" />
              <Sep />
              <Stat n={others} label="others" />
            </>
          )}
        </div>

        <div className="flex items-center gap-1">{pager}</div>

        {rowsPerPage !== undefined && onRowsPerPageChange && (
          <RowsPerPageSelect
            value={rowsPerPage}
            options={rowsPerPageOptions}
            onChange={onRowsPerPageChange}
          />
        )}
      </div>
    </div>
  );
}

/** Placeholder controls for surfaces that haven't wired client-side paging. */
function StaticPager() {
  return (
    <>
      <PageButton aria-label="Previous page">
        <Icon name="chevron_left" size={20} />
      </PageButton>
      <ActivePage>1</ActivePage>
      <PageButton aria-label="Next page">
        <Icon name="chevron_right" size={20} />
      </PageButton>
    </>
  );
}

/** Wired page controls — prev/next within bounds + numbered pages with ellipsis. */
function Pager({
  page,
  pageCount,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}) {
  const atStart = page <= 1;
  const atEnd = page >= pageCount;
  return (
    <>
      <PageButton
        aria-label="Previous page"
        disabled={atStart}
        onClick={() => onPageChange(page - 1)}
      >
        <Icon name="chevron_left" size={20} />
      </PageButton>
      {pageItems(page, pageCount).map((item, i) =>
        item === "ellipsis" ? (
          <span
            key={`gap-${i}`}
            aria-hidden
            className="flex h-8 min-w-8 items-center justify-center px-1 text-small text-grey-500"
          >
            …
          </span>
        ) : item === page ? (
          <ActivePage key={item}>{item}</ActivePage>
        ) : (
          <PageNumber key={item} page={item} onClick={() => onPageChange(item)} />
        ),
      )}
      <PageButton aria-label="Next page" disabled={atEnd} onClick={() => onPageChange(page + 1)}>
        <Icon name="chevron_right" size={20} />
      </PageButton>
    </>
  );
}

/**
 * Compact page list: show every page up to 7, otherwise collapse the middle
 * with ellipses, always keeping first + last + the current page's neighbours.
 */
function pageItems(current: number, count: number): (number | "ellipsis")[] {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const items: (number | "ellipsis")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(count - 1, current + 1);
  if (start > 2) items.push("ellipsis");
  for (let p = start; p <= end; p++) items.push(p);
  if (end < count - 1) items.push("ellipsis");
  items.push(count);
  return items;
}

function RowsPerPageSelect({
  value,
  options,
  onChange,
}: {
  value: number;
  options: readonly number[];
  onChange: (n: number) => void;
}) {
  return (
    <label className="flex items-center gap-1 text-tiny text-grey-900">
      <span>Rows per page:</span>
      <Select
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        variant="outlined"
        size="small"
        sx={{
          fontSize: "12px",
          fontWeight: 500,
          color: "#2d343c",
          backgroundColor: "#ffffff",
          "& .MuiSelect-select": {
            padding: "2px 24px 2px 6px",
            minHeight: "unset",
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e5ebf1", // grey-200
          },
          "&:hover": {
            backgroundColor: "#f6f8fa", // grey-50
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#e5ebf1",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#a7b0ba", // grey-500
            borderWidth: "1px",
          },
          "& .MuiSelect-icon": {
            color: "#73808e", // grey-600
            right: "2px",
          },
          borderRadius: "4px",
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              border: "1px solid #e5ebf1",
              borderRadius: "4px",
              boxShadow: "0px 8px 32px #2d343c14", // shadow-soft
              "& .MuiMenuItem-root": {
                fontSize: "12px",
                fontWeight: 500,
                minHeight: "unset",
                paddingTop: "4px",
                paddingBottom: "4px",
              },
            },
          },
        }}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </label>
  );
}

function Sep() {
  return <span className="font-normal text-grey-300">|</span>;
}

/** Singular form for a count label when n === 1 (e.g. "1 organization",
 *  "1 entity", "1 match"). Covers the -ies / -es / -s plural endings used by
 *  the labels passed here (entities, matches, names, individuals, …). */
function singularize(plural: string): string {
  if (plural.endsWith("ies")) return plural.slice(0, -3) + "y";
  if (/(ch|sh|s|x|z)es$/.test(plural)) return plural.slice(0, -2);
  if (plural.endsWith("s")) return plural.slice(0, -1);
  return plural;
}

function Stat({ n, label }: { n: number; label: string }) {
  return (
    <span className="font-normal text-grey-900">
      {/* Show the real count, 0 included — a zero breakdown is an accurate
          "0 organizations", not missing data (was "-", flagged in QA). */}
      {formatNumber(n)} {n === 1 ? singularize(label) : label}
    </span>
  );
}

function PageButton({
  children,
  disabled,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={[
        "flex h-8 min-w-8 items-center justify-center rounded-[4px] px-1",
        disabled ? "cursor-default text-grey-300" : "text-grey-900 hover:bg-grey-100",
      ].join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}

function PageNumber({ page, onClick }: { page: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Go to page ${page}`}
      className="flex h-8 min-w-8 items-center justify-center rounded-[4px] px-1 text-small font-medium text-grey-900 transition-colors hover:bg-grey-100 hover:text-black"
    >
      {page}
    </button>
  );
}

function ActivePage({ children }: { children: React.ReactNode }) {
  return (
    <span
      aria-current="page"
      className="flex h-8 min-w-8 items-center justify-center rounded-[4px] border border-grey-200 bg-green-100 px-1 text-small font-medium text-green-700"
    >
      {children}
    </span>
  );
}
